import { Saga } from 'redux-saga'
import {
  ActionPattern,
  all,
  call,
  delay,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import { getKeycloakToken } from 'src/helpers/KeycloakHelpers'
import { PayloadAction } from '@reduxjs/toolkit'

import Router from '../../routes/router'
import routes from '../../routes/definitions'
import { actions, selectors } from '..'
import {
  getAuthCookie,
  removeAuthCookie,
  setAuthCookie,
} from '../../helpers/CookieHelpers'
import ApiSagas from '../api/sagas'
import { Planning } from '../../graphql/Services'
import { currentPlan, me } from '../../graphql/Services/User/queries'
import { ServiceRequestAction } from '../../helpers/ReduxHelpers'
import {
  associateToken,
  onboard,
  starRecipes,
  unstarRecipes,
  updateNotificationPreferences,
  updatePushPreferences,
} from '../../graphql/Services/User/mutations'
import { User } from '../../types/fam-graphql'
import OnboardingSagas from '../onboarding/sagas'
import {
  requestNotificationPermission,
  wpListenToSubscribeEvent,
  wpSendSelfNotification,
  wpSetSubscribeToNotifications,
  wpSetUnsubscribeFromNotifications,
  wpEnsureProperSubscription,
} from '../../utils/WonderPushUtils'
import PlanSagas from '../plan/sagas'
import updateDownloadPreferences from '../../graphql/Services/User/mutations/updateDownloadPreferences'

import {
  addRecipeToFavorites,
  removeRecipeFromFavorites,
  setToken,
  setUser,
} from './types/actions'

export default class UserSaga {
  static *init() {
    const cookie = yield call(getAuthCookie)
    const token = yield select(selectors.user.token)
    console.log('>>>> UserSaga - init', { cookie, token })

    if (cookie) {
      yield put(actions.user.setToken(cookie))
    } else {
      yield put(actions.user.setUser(null))
    }

    yield call(UserSaga.checkKeyCloak)
    const redirect = yield call(UserSaga.redirectLogged)
    if (redirect) {
      // wait for page to change
      yield call(
        () =>
          new Promise((resolve) => {
            Router.events.on('routeChangeComplete', (url: string) => {
              console.log('redirection complete', url)
              resolve()
            })
          })
      )
      console.log('after redirection, setUserIsInit')
    }
    yield put(actions.user.setIsInit(true))
    const isInit = yield select(selectors.user.isInit)
    console.log('user is init', isInit)

    const afterLoginJobs: PayloadAction[] = yield select(
      selectors.app.afterLoginJobs
    )
    for (const job of afterLoginJobs) {
      yield put({
        type: job.type,
        payload: job.payload,
      })
    }
    yield put(actions.app.clearAfterLoginJobs())
  }

  static *redirectLogged(ctx?: any) {
    try {
      const href = window.location.href
      const match = Router.match(href)
      const pathname = ctx?.pathname || match?.route?.page

      console.log('current page', { pathname, match: match?.route?.page })

      const isUserOnboarded = yield select(selectors.user.isUserOnboarded)
      const shouldBeOnboardedPages = Object.values(routes).find(
        (r: any) => r.shouldBeOnboarded && r.component === pathname
      )
      const shouldntBeOnboardedPages = Object.values(routes).find(
        (r: any) => r.shouldBeOnboarded === false && r.component === pathname
      )

      console.log('>>>> UserSaga - redirectLogged', {
        isUserOnboarded,
        shouldBeOnboardedPages,
        shouldntBeOnboardedPages,
      })

      if (shouldBeOnboardedPages && !isUserOnboarded) {
        console.log('UserSagas -> Redirect to OnBoarding')
        Router.pushRoute(routes.onboarding.name)
        return true
      }

      if (shouldntBeOnboardedPages && isUserOnboarded) {
        console.log('UserSagas -> Redirect to Planning')
        Router.pushRoute(routes.planning.name)
        return true
      }
    } catch (e) {
      console.log('Error in redirectLogged', e)
    }
  }

  static *checkKeyCloak() {
    console.log('[Keycloak] init')

    const previousToken = yield select(selectors.user.token)
    console.log('[Keycloak] previous token', previousToken)

    const previousTokenIsAnonymous = previousToken?.length < 1000
    console.log('[Keycloak] previousTokenIsAnonymous', previousTokenIsAnonymous)

    const keycloakToken: string | false = yield call(
      getKeycloakToken,
      previousToken,
      previousTokenIsAnonymous
    )
    console.log('[Keycloak] getToken', keycloakToken)

    // if keycloak not connected
    if (typeof keycloakToken !== 'string') {
      yield put(actions.user.setIsKeycloakProcessDone(true))
      // is deconnected from keycloak & not anonymous -> remove cookie & redirectLoggued
      console.log('[Keycloak] not connected')
      if (previousTokenIsAnonymous) {
        yield put(actions.user.refreshUser())
        yield take(actions.user.setUser)
        yield call(PlanSagas.loadCurrentPlan)
      } else if (previousToken) {
        // previous token is a keycloak token so use to refresh user
        yield put(actions.user.setKeycloakToken(previousToken))
        yield put(actions.user.refreshUser())
        yield take(actions.user.setUser)
        yield call(PlanSagas.loadCurrentPlan)
      } else {
        yield put(actions.user.setToken(''))
        yield put(actions.user.setUser(null))
      }
      return
    } else {
      if (previousTokenIsAnonymous) {
        yield call(PlanSagas.loadReducedCurrentPlan)
      }
    }

    // keycloak connected
    const previousPlan = yield select(selectors.user.currentPlan)
    const hasPreviousGeneratedPlan = !!previousPlan
    console.log('[Keycloak] hasPreviousGeneratedPlan', hasPreviousGeneratedPlan)

    // set token & keycloakToken
    yield put(actions.user.setKeycloakToken(keycloakToken))

    // if connected to keycloak now and was anonymous before, we associate the token
    if (hasPreviousGeneratedPlan && previousTokenIsAnonymous && previousToken) {
      const associateRs = yield call(
        ApiSagas.mutate,
        associateToken({ token: previousToken })
      )
      console.log('[Keycloak] associate token', associateRs)
    }

    yield put(actions.user.refreshUser())
    yield take(actions.user.setUser)
    //yield call(PlanSagas.loadCurrentPlan)
    yield put(actions.user.setIsKeycloakProcessDone(true))
  }

  static *generatePlanning() {
    const currentPlan = yield select(selectors.user.currentPlan)
    yield put(actions.user.setIsGeneratingPlanning(true))
    const messages: string[] = yield select(
      selectors.settings.generatingMessage
    )
    yield put(
      actions.user.setGenerationMessage(
        messages[Math.floor(Math.random() * (messages?.length || 0))]
      )
    )

    // call with a minimum wait of 4s
    const [{ data: resultDrawPlan }] = yield all([
      call(
        ApiSagas.mutate,
        Planning.mutations.drawPlan({
          ...(currentPlan?.id && {
            previous: currentPlan?.id,
          }),
        })
      ),
      delay(4000),
    ])

    yield put(actions.user.setCurrentPlan(resultDrawPlan))

    // redirect to planning
    Router.pushRoute(routes.planning.name)
  }

  static *refreshUser() {
    try {
      const results = yield call(ApiSagas.query, me())
      if (!results.errors) {
        yield put(actions.user.setUser(results?.data))
      } else {
        yield put(actions.user.setKeycloakToken(''))
        yield put(actions.user.setToken(''))
        yield put(actions.user.setUser(null))
      }
    } catch (e) {
      console.log('Error in refreshUser', e)
      yield put(actions.user.setKeycloakToken(''))
      yield put(actions.user.setToken(''))
      yield put(actions.user.setUser(null))
    }
  }

  static saveToken(token: string) {
    if (token) {
      setAuthCookie(token)
    } else {
      removeAuthCookie()
    }
  }

  static *onSetUser(_action: setUser) {
    const token = yield select(selectors.user.token)
    yield call(UserSaga.saveToken, token || '')
  }

  static *onSetToken(action: setToken) {
    yield call(UserSaga.saveToken, action?.payload)
  }

  static *updateObjectivesHandler(action: ServiceRequestAction) {
    yield put(actions.user.setIsGeneratingPlanning(true))
    const values = action.payload?.params
    const nutritionalValues = yield select(
      selectors.user.nutritionalPreferences
    )

    const input = yield call(OnboardingSagas.getOnboardPayload, {
      ...nutritionalValues,
      ...values,
    })
    const results = yield call(ApiSagas.mutate, onboard(input))

    if (!results.error) {
      yield put(actions.user.updateObjectivesSuccess({}))
      yield put(actions.user.setUser(results.data as User))
      yield put(actions.user.generatePlanning())
    } else {
      yield put(actions.user.updateObjectivesError(results.error))
    }
  }

  static *updatePreferencesHandler(action: ServiceRequestAction) {
    const values = action.payload?.params
    const mustKeepPlanning = action.payload?.params?.keepPlanning
    if (!mustKeepPlanning) yield put(actions.user.setIsGeneratingPlanning(true))
    const nutritionalValues = yield select(
      selectors.user.nutritionalPreferences
    )

    const input = yield call(OnboardingSagas.getOnboardPayload, {
      ...nutritionalValues,
      ...values,
    })
    const results = yield call(ApiSagas.mutate, onboard(input))

    if (!results.error) {
      yield put(actions.user.updatePreferencesSuccess({}))
      if (values?.express !== undefined) {
        yield put(actions.user.setHasJustModifiedExpressPref('update'))
      }
      yield put(actions.user.setUser(results.data as User))

      if (!mustKeepPlanning) {
        yield put(actions.user.generatePlanning())
      } else {
        const result = yield call(ApiSagas.query, currentPlan())
        if (!result.error) {
          yield put(actions.user.setCurrentPlan(result.data))
          Router.pushRoute(routes.planning.name)
        }
      }
    } else {
      yield put(actions.user.updatePreferencesError(results.error))
    }
  }

  static *updateFavoriteRecipes(action: ServiceRequestAction) {
    const favoriteRecipesIds: string[] = yield select(
      selectors.user.favoriteRecipesIds
    ) || []
    if (action.payload) {
      const recipeId = action.payload
      const isFavorite = favoriteRecipesIds.includes(recipeId)
      if (isFavorite) {
        yield put(actions.user.removeRecipeFromFavorites(recipeId))
      } else {
        yield put(actions.user.addRecipeToFavorites(recipeId))
      }
    }
  }

  static *addRecipeToFavorites(action: addRecipeToFavorites) {
    const results = yield call(
      ApiSagas.mutate,
      starRecipes({ recipes: [action.payload] })
    )

    if (!results.error) {
      yield put(actions.user.setFavoriteRecipes(results.data))
    }
  }

  static *removeRecipeFromFavorites(action: removeRecipeFromFavorites) {
    const results = yield call(
      ApiSagas.mutate,
      unstarRecipes({ recipes: [action.payload] })
    )

    if (!results.error) {
      yield put(actions.user.setFavoriteRecipes(results.data))
    }
  }

  static *updateNotificationHandler(action: ServiceRequestAction) {
    const res = yield call(
      ApiSagas.mutate,
      updateNotificationPreferences({
        should_receive_notifications:
          action.payload?.params.shouldReceiveNotifications,
        notification_frequency: action.payload?.params.notificationFrequency,
      })
    )

    if (!res.error) {
      yield put(
        actions.user.setNotificationPreferences({
          shouldReceiveNotifications: res.data.should_receive_notifications,
          notificationFrequency: res.data.notification_frequency,
        })
      )

      const id: string = yield select(selectors.user.id)
      if (res.data.should_receive_notifications) {
        try {
          const permissionResult = yield call(requestNotificationPermission)
          if (permissionResult === 'granted') {
            console.log('[WonderPush] Setting up subscription...')
            const subscriptionSuccess = yield call(
              wpEnsureProperSubscription,
              id
            )
            if (!subscriptionSuccess) {
              console.error(
                '[WonderPush] Failed to ensure proper subscription setup'
              )
            }
          } else {
            yield call(wpSetUnsubscribeFromNotifications)
          }
        } catch (e) {
          console.error('[WonderPush] Error in notification setup:', e)
        }
      } else {
        yield call(wpSetUnsubscribeFromNotifications)
      }
    }
  }

  static *sendSelfNotification() {
    try {
      const res = yield call(requestNotificationPermission)
      if (res === 'granted') {
        yield call(wpSetSubscribeToNotifications)
        const isSubscribed = yield call(wpListenToSubscribeEvent)
        if (isSubscribed) {
          yield delay(3000)
          yield call(wpSendSelfNotification)
        }
      } else {
        yield call(wpSetUnsubscribeFromNotifications)
      }
    } catch (e) {
      console.log('[WonderPush] Error', e)
    }
  }

  static *updatePushPreferencesHandler(action: ServiceRequestAction) {
    const res = yield call(
      ApiSagas.mutate,
      updatePushPreferences(action.payload?.params)
    )

    if (!res.error) {
      yield put(
        actions.user.setPushPreferences({ ...res.data.push_preferences })
      )
    }
  }

  static *updateDownloadPreferencesHandler(action: ServiceRequestAction) {
    const res = yield call(
      ApiSagas.mutate,
      updateDownloadPreferences(action.payload)
    )

    if (!res.error) {
      yield put(
        actions.user.setHasDownloadMenu(res.data.has_download_menu || false)
      )
    }
  }

  static *loop() {
    yield all([
      takeLatest(
        actions.user.generatePlanning as ActionPattern,
        this.generatePlanning as Saga
      ),
      takeLatest(
        actions.user.refreshUser as ActionPattern,
        this.refreshUser as Saga
      ),
      takeLatest(actions.user.setUser as ActionPattern, this.onSetUser as Saga),
      takeLatest(
        actions.user.setToken as ActionPattern,
        this.onSetToken as Saga
      ),
      takeLatest(
        actions.user.setKeycloakToken as ActionPattern,
        this.onSetToken as Saga
      ),
      takeLatest(
        actions.user.updateObjectivesRequest as ActionPattern,
        this.updateObjectivesHandler as Saga
      ),
      takeLatest(
        actions.user.updatePreferencesRequest as ActionPattern,
        this.updatePreferencesHandler as Saga
      ),
      takeLatest(
        actions.user.updateFavoriteRecipes as ActionPattern,
        this.updateFavoriteRecipes as Saga
      ),
      takeLatest(
        actions.user.addRecipeToFavorites as ActionPattern,
        this.addRecipeToFavorites as Saga
      ),
      takeLatest(
        actions.user.removeRecipeFromFavorites as ActionPattern,
        this.removeRecipeFromFavorites as Saga
      ),
      takeLatest(
        actions.user.updateNotificationRequest as ActionPattern,
        this.updateNotificationHandler as Saga
      ),
      takeLatest(
        actions.user.updatePushPreferencesRequest as ActionPattern,
        this.updatePushPreferencesHandler as Saga
      ),
      takeLatest(
        actions.user.sendSelfNotification as ActionPattern,
        this.sendSelfNotification as Saga
      ),
      takeLatest(
        actions.user.updateDownloadPreferencesRequest as ActionPattern,
        this.updateDownloadPreferencesHandler as Saga
      ),
    ])
  }
}
